import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Check, X } from "@phosphor-icons/react";

export default function Checkout() {
  const { items, subtotal } = useCart();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", address: "", city: "", zip: "", country: "US" });
  const [promoCode, setPromoCode] = useState("");
  const [promo, setPromo] = useState(null);
  const [promoBusy, setPromoBusy] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("card");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const applyPromo = async () => {
    if (!promoCode.trim()) return;
    setPromoBusy(true);
    try {
      const { data } = await api.post("/promo/validate", { code: promoCode, subtotal });
      setPromo(data);
      toast.success(data.label);
    } catch (err) {
      setPromo(null);
      toast.error(err.response?.data?.detail || "Invalid code");
    } finally { setPromoBusy(false); }
  };

  const removePromo = () => { setPromo(null); setPromoCode(""); };

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) return toast.error("Your bag is empty");
    setBusy(true);
    try {
      const { data } = await api.post("/checkout", {
        items: items.map(i => ({ product_id: i.product.id, quantity: i.quantity, size: i.size, color: i.color, is_gift: !!i.is_gift, gift_note: i.gift_note || null, gift_parent: i.gift_parent || null, gift_recipient_name: i.gift_recipient_name || null, gift_recipient_email: i.gift_recipient_email || null })),
        origin_url: window.location.origin,
        shipping_address: form,
        promo_code: promo?.code || null,
        payment_method: paymentMethod,
      });
      if (data.cod) {
        window.location.href = data.redirect_url;
      } else {
        window.location.href = data.checkout_url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Checkout failed");
      setBusy(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4" data-testid="empty-checkout">
        <p className="font-serif text-3xl">Your bag is empty.</p>
        <button onClick={() => nav("/shop")} className="overline underline underline-offset-4">Continue shopping</button>
      </div>
    );
  }

  const shipping = subtotal >= 5000 ? 0 : 200;
  const discount = promo?.discount || 0;
  const total = Math.max(0, subtotal - discount) + shipping;

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 grid lg:grid-cols-2 gap-16" data-testid="checkout-page">
      <div>
        <div className="overline text-[#4A1525] mb-4">Checkout</div>
        <h1 className="font-serif text-5xl mb-10">Shipping details</h1>
        <form onSubmit={submit} className="space-y-6">
          <div>
            <label className="overline block mb-2">Full name</label>
            <input required value={form.name} onChange={set("name")} className="w-full border-b border-[#E6DED5] bg-transparent py-3 focus:outline-none focus:border-[#4A1525]" data-testid="ship-name" />
          </div>
          <div>
            <label className="overline block mb-2">Email</label>
            <input type="email" required value={form.email} onChange={set("email")} className="w-full border-b border-[#E6DED5] bg-transparent py-3 focus:outline-none focus:border-[#4A1525]" data-testid="ship-email" />
          </div>
          <div>
            <label className="overline block mb-2">Address</label>
            <input required value={form.address} onChange={set("address")} className="w-full border-b border-[#E6DED5] bg-transparent py-3 focus:outline-none focus:border-[#4A1525]" data-testid="ship-address" />
          </div>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="overline block mb-2">City</label>
              <input required value={form.city} onChange={set("city")} className="w-full border-b border-[#E6DED5] bg-transparent py-3 focus:outline-none focus:border-[#4A1525]" data-testid="ship-city" />
            </div>
            <div>
              <label className="overline block mb-2">Postal code</label>
              <input required value={form.zip} onChange={set("zip")} className="w-full border-b border-[#E6DED5] bg-transparent py-3 focus:outline-none focus:border-[#4A1525]" data-testid="ship-zip" />
            </div>
          </div>
          <div>
            <label className="overline block mb-2">Country</label>
            <input required value={form.country} onChange={set("country")} className="w-full border-b border-[#E6DED5] bg-transparent py-3 focus:outline-none focus:border-[#4A1525]" data-testid="ship-country" />
          </div>

          {/* Payment method */}
          <div className="pt-6 mt-2 border-t border-[#E6DED5]">
            <div className="overline mb-4">Payment method</div>
            <div className="space-y-3">
              <label className={`flex items-start gap-4 p-4 border cursor-pointer transition-colors ${paymentMethod === "card" ? "border-[#4A1525] bg-[#F0EBE1]" : "border-[#E6DED5]"}`} data-testid="pm-card">
                <input type="radio" name="pm" value="card" checked={paymentMethod === "card"} onChange={() => setPaymentMethod("card")} className="mt-1" />
                <div>
                  <div className="font-serif text-lg">Pay with card</div>
                  <div className="overline text-[#5C5C5C] mt-1">Secure checkout via Stripe · Visa, Mastercard, Amex</div>
                </div>
              </label>
              <label className={`flex items-start gap-4 p-4 border cursor-pointer transition-colors ${paymentMethod === "cod" ? "border-[#4A1525] bg-[#F0EBE1]" : "border-[#E6DED5]"}`} data-testid="pm-cod">
                <input type="radio" name="pm" value="cod" checked={paymentMethod === "cod"} onChange={() => setPaymentMethod("cod")} className="mt-1" />
                <div>
                  <div className="font-serif text-lg">Cash on Delivery</div>
                  <div className="overline text-[#5C5C5C] mt-1">Pay in cash when your parcel arrives</div>
                </div>
              </label>
            </div>
          </div>

          <button type="submit" disabled={busy} className="w-full bg-[#4A1525] text-[#FAF8F5] py-4 overline hover:bg-[#360F1B] transition-colors disabled:opacity-50 mt-4" data-testid="pay-btn">
            {busy ? "Placing order..." : paymentMethod === "cod" ? `Place order — ₹${total.toFixed(0)}` : `Pay with card — ₹${total.toFixed(0)}`}
          </button>
          <p className="text-xs text-[#5C5C5C] text-center">
            {paymentMethod === "cod" ? "Your order will be confirmed and prepared for delivery. Please have exact change ready." : "You'll be redirected to Stripe for secure payment. Test card: 4242 4242 4242 4242."}
          </p>
        </form>
      </div>

      <div className="bg-[#F0EBE1] p-8 lg:p-12 h-fit">
        <h2 className="font-serif text-2xl mb-6">Order summary</h2>
        <div className="space-y-4 mb-6">
          {items.map((it, idx) => (
            <div key={idx} className="flex gap-4">
              <img src={it.product.images[0]} alt={it.product.name} className="w-16 h-20 object-cover" />
              <div className="flex-1">
                <div className="font-serif">{it.product.name}</div>
                <div className="overline text-[#5C5C5C]">{it.size} · {it.color} · ×{it.quantity}</div>
              </div>
              <div className="text-sm">₹{(it.product.price * it.quantity).toFixed(0)}</div>
            </div>
          ))}
        </div>
        <div className="border-t border-[#E6DED5] pt-4 space-y-2 text-sm">
          <div className="flex justify-between"><span>Subtotal</span><span data-testid="sum-subtotal">₹{subtotal.toFixed(0)}</span></div>
          {promo && (
            <div className="flex justify-between text-[#4A1525]" data-testid="applied-discount">
              <span className="flex items-center gap-2">Promo · {promo.code}</span>
              <span>−${discount.toFixed(0)}</span>
            </div>
          )}
          <div className="flex justify-between"><span>Shipping</span><span>{shipping === 0 ? "Complimentary" : `₹${shipping}`}</span></div>
          <div className="flex justify-between font-serif text-xl pt-2 border-t border-[#E6DED5] mt-2"><span>Total</span><span data-testid="sum-total">₹{total.toFixed(0)}</span></div>
        </div>

        {/* Promo code */}
        <div className="mt-6 pt-6 border-t border-[#E6DED5]">
          <div className="overline mb-3">Promo code</div>
          {promo ? (
            <div className="flex items-center justify-between bg-[#FAF8F5] border border-[#4A1525] px-3 py-2" data-testid="promo-applied">
              <span className="flex items-center gap-2 text-sm text-[#4A1525]"><Check size={16} weight="light" /> {promo.code} — {promo.label}</span>
              <button type="button" onClick={removePromo} className="text-[#5C5C5C]" data-testid="remove-promo" aria-label="Remove"><X size={16} weight="light" /></button>
            </div>
          ) : (
            <div className="flex gap-2">
              <input value={promoCode} onChange={(e) => setPromoCode(e.target.value.toUpperCase())} placeholder="Try RAVISH15" className="flex-1 border border-[#E6DED5] bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#4A1525]" data-testid="promo-input" />
              <button type="button" onClick={applyPromo} disabled={promoBusy || !promoCode.trim()} className="px-5 py-2 overline border border-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-[#FAF8F5] transition-colors disabled:opacity-50" data-testid="apply-promo">
                {promoBusy ? "..." : "Apply"}
              </button>
            </div>
          )}
          <p className="text-xs text-[#5C5C5C] mt-2">Try <b>RAVISH15</b> (15% off), <b>WELCOME10</b> (₹1,000 off ₹7,500+), or <b>SILK25</b> (25% off ₹20,000+).</p>
        </div>
      </div>
    </div>
  );
}
